import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import type { Resolver } from 'react-hook-form'

import type {
    Expense,
    ExpensePaymentMethod,
    ExpensePaymentStatus,
} from '@/lib/api/expense-api'
import type { ExpensesSearch } from '@/lib/schemas/expense-search'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useDebounced } from '@/hooks/use-debounced'
import { useExpenseCategories } from '@/hooks/use-expense-category-query'
import {
    useCreateExpense,
    useDeleteExpense,
    useExpenses,
    useUpdateExpense,
} from '@/hooks/use-expense-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { normalizeExpensesSearch } from '@/lib/schemas/expense-search'
import { createExpenseSchema } from '@/lib/schemas/expense-schemas'

const PER_PAGE = 50

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/expenses',
)({
    component: ExpensesPage,
    // Trang + bộ lọc đẩy lên search param → reload / share link / back giữ
    // nguyên trạng thái đang xem.
    validateSearch: normalizeExpensesSearch,
})

type FormValues = {
    category_id: string
    vendor_name: string
    amount: number
    expense_date: string
    payment_method: ExpensePaymentMethod
    payment_status: ExpensePaymentStatus
    note?: string
}

function todayYMD(): string {
    return new Date().toISOString().slice(0, 10)
}

function ExpensesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('expenses.write')
    const canDelete = hasPermission('expenses.delete')

    const navigate = useNavigate({ from: '/accounting/expenses' })
    const urlSearch = Route.useSearch()
    const setSearch = (
        updater: (prev: ExpensesSearch) => ExpensesSearch,
        opts?: { replace?: boolean },
    ) => void navigate({ search: updater, replace: opts?.replace ?? false })

    // Ô tìm kiếm giữ state cục bộ để gõ mượt, debounce rồi mới đẩy lên URL —
    // tránh spam history và một request list cho mỗi ký tự.
    const [searchInput, setSearchInput] = useState(urlSearch.q ?? '')
    const debouncedSearch = useDebounced(searchInput, 300)
    useEffect(() => {
        if (debouncedSearch === (urlSearch.q ?? '')) return
        setSearch(
            (prev) => ({
                ...prev,
                q: debouncedSearch || undefined,
                page: undefined,
            }),
            { replace: true },
        )
    }, [debouncedSearch])
    // Kéo ngược khi URL đổi từ ngoài (back/forward, deep-link).
    useEffect(() => {
        setSearchInput(urlSearch.q ?? '')
    }, [urlSearch.q])

    const categoryId = urlSearch.category_id ?? ''
    const paymentMethod = urlSearch.payment_method ?? ''
    const paymentStatus = urlSearch.payment_status ?? ''
    const page = urlSearch.page ?? 1

    const [editing, setEditing] = useState<Expense | null>(null)
    const [open, setOpen] = useState(false)

    const { data: catRes } = useExpenseCategories({
        is_active: true,
        per_page: 100,
    })
    const categories = catRes?.data ?? []

    const { data: listRes } = useExpenses({
        search: urlSearch.q || undefined,
        category_id: categoryId || undefined,
        payment_method: paymentMethod || undefined,
        payment_status: paymentStatus || undefined,
        page,
        per_page: PER_PAGE,
    })
    const rows = listRes?.data ?? []
    const meta = listRes?.meta
    const lastPage = Math.max(1, meta?.total_pages ?? 1)

    // Xoá dòng cuối của trang cuối làm trang hiện tại vượt quá tổng số trang —
    // kéo về trang cuối còn dữ liệu thay vì hiển thị bảng rỗng. Dùng replace để
    // nút back không rơi lại đúng trang vừa biến mất.
    useEffect(() => {
        if (!meta || page <= lastPage) return
        setSearch(
            (prev) => ({
                ...prev,
                page: lastPage > 1 ? lastPage : undefined,
            }),
            { replace: true },
        )
    }, [meta, page, lastPage])

    const createMut = useCreateExpense()
    const updateMut = useUpdateExpense(editing?.id ?? '')
    const deleteMut = useDeleteExpense()

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">{m.expense_title()}</h1>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.expense_new()}
                    </Button>
                )}
            </div>

            {/* Filter bar */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <Input
                    placeholder={m.expense_vendor_name()}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
                <Select
                    value={categoryId || 'all'}
                    onValueChange={(v) =>
                        setSearch((prev) => ({
                            ...prev,
                            category_id: v === 'all' ? undefined : v,
                            page: undefined,
                        }))
                    }
                >
                    <SelectTrigger>
                        <SelectValue
                            placeholder={m.expense_filter_all_categories()}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.expense_filter_all_categories()}
                        </SelectItem>
                        {categories.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={paymentMethod || 'all'}
                    onValueChange={(v) =>
                        setSearch((prev) => ({
                            ...prev,
                            payment_method:
                                v === 'all'
                                    ? undefined
                                    : (v as ExpensePaymentMethod),
                            page: undefined,
                        }))
                    }
                >
                    <SelectTrigger>
                        <SelectValue
                            placeholder={m.expense_filter_all_methods()}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.expense_filter_all_methods()}
                        </SelectItem>
                        <SelectItem value="cash">
                            {m.expense_payment_method_cash()}
                        </SelectItem>
                        <SelectItem value="bank_transfer">
                            {m.expense_payment_method_bank_transfer()}
                        </SelectItem>
                    </SelectContent>
                </Select>
                <Select
                    value={paymentStatus || 'all'}
                    onValueChange={(v) =>
                        setSearch((prev) => ({
                            ...prev,
                            payment_status:
                                v === 'all'
                                    ? undefined
                                    : (v as ExpensePaymentStatus),
                            page: undefined,
                        }))
                    }
                >
                    <SelectTrigger>
                        <SelectValue
                            placeholder={m.expense_filter_all_status()}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.expense_filter_all_status()}
                        </SelectItem>
                        <SelectItem value="paid">
                            {m.expense_payment_status_paid()}
                        </SelectItem>
                        <SelectItem value="unpaid">
                            {m.expense_payment_status_unpaid()}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.expense_date()}</TableHead>
                        <TableHead>{m.expense_vendor_name()}</TableHead>
                        <TableHead>{m.expense_category_title()}</TableHead>
                        <TableHead className="text-right">
                            {m.expense_amount()}
                        </TableHead>
                        <TableHead>{m.expense_payment_method()}</TableHead>
                        <TableHead>{m.expense_payment_status()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={7}
                                className="text-center text-muted-foreground py-8"
                            >
                                {m.expense_list_empty()}
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>
                                {row.expense_date.slice(0, 10)}
                            </TableCell>
                            <TableCell>{row.vendor_name}</TableCell>
                            <TableCell>{row.category?.name ?? '—'}</TableCell>
                            <TableCell className="text-right font-mono">
                                {formatVnd(row.amount)}
                            </TableCell>
                            <TableCell>
                                {row.payment_method === 'cash'
                                    ? m.expense_payment_method_cash()
                                    : m.expense_payment_method_bank_transfer()}
                            </TableCell>
                            <TableCell>
                                <Badge
                                    variant={
                                        row.payment_status === 'paid'
                                            ? 'default'
                                            : 'secondary'
                                    }
                                >
                                    {row.payment_status === 'paid'
                                        ? m.expense_payment_status_paid()
                                        : m.expense_payment_status_unpaid()}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                            setEditing(row)
                                            setOpen(true)
                                        }}
                                    >
                                        <Pencil className="size-4" />
                                    </Button>
                                )}
                                {canDelete && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    m.expense_delete_confirm(),
                                                )
                                            ) {
                                                deleteMut.mutate(row.id)
                                            }
                                        }}
                                    >
                                        <Trash2 className="size-4" />
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {meta && meta.total > 0 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.expense_total_count({ count: String(meta.total) })}
                    </span>
                    {meta.total_pages > 1 && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page === 1}
                                onClick={() =>
                                    setSearch((prev) => ({
                                        ...prev,
                                        page:
                                            page - 1 > 1 ? page - 1 : undefined,
                                    }))
                                }
                            >
                                {m.common_pagination_prev()}
                            </Button>
                            <span className="flex items-center px-2">
                                {page} / {meta.total_pages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page >= meta.total_pages}
                                onClick={() =>
                                    setSearch((prev) => ({
                                        ...prev,
                                        page: page + 1,
                                    }))
                                }
                            >
                                {m.common_pagination_next()}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? m.expense_edit() : m.expense_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <ExpenseForm
                        initial={editing}
                        categories={categories}
                        onSubmit={async (values) => {
                            if (editing) {
                                await updateMut.mutateAsync(values)
                            } else {
                                await createMut.mutateAsync(values)
                            }
                            setOpen(false)
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}

function ExpenseForm({
    initial,
    categories,
    onSubmit,
    submitting,
}: {
    initial: Expense | null
    categories: ReadonlyArray<{ id: string; name: string }>
    onSubmit: (v: FormValues) => Promise<void>
    submitting: boolean
}) {
    const schema = createExpenseSchema()
    const form = useForm<FormValues>({
        // zodResolver overloads expect either zod v3 or zod v4 (minor=0) schema
        // types; expense-schemas.ts uses zod v4 which reports minor=3 in its
        // internal type brand. Cast through unknown to bypass the overload check —
        // the runtime behavior is identical.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resolver: zodResolver(schema as any) as Resolver<FormValues>,
        defaultValues: {
            category_id: initial?.category_id ?? categories[0]?.id ?? '',
            vendor_name: initial?.vendor_name ?? '',
            amount: initial?.amount ?? 0,
            expense_date: initial?.expense_date?.slice(0, 10) ?? todayYMD(),
            payment_method:
                (initial?.payment_method as ExpensePaymentMethod) ?? 'cash',
            payment_status:
                (initial?.payment_status as ExpensePaymentStatus) ?? 'unpaid',
            note: initial?.note ?? '',
        },
    })
    const err = form.formState.errors
    return (
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
                <Label>{m.expense_category_title()}</Label>
                <Select
                    value={form.watch('category_id')}
                    onValueChange={(v) => form.setValue('category_id', v)}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {categories.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {err.category_id && (
                    <p className="text-xs text-red-500">
                        {err.category_id.message}
                    </p>
                )}
            </div>
            <div>
                <Label>{m.expense_vendor_name()}</Label>
                <Input {...form.register('vendor_name')} />
                {err.vendor_name && (
                    <p className="text-xs text-red-500">
                        {err.vendor_name.message}
                    </p>
                )}
            </div>
            <div>
                <Label>{m.expense_amount()}</Label>
                <Input
                    type="number"
                    inputMode="numeric"
                    // step=1, not 1000: a non-1 step makes the browser reject any
                    // amount that is not a multiple of it ("nearest valid values
                    // are 1270000 and 1271000"), blocking exact đồng entry.
                    step={1}
                    {...form.register('amount', { valueAsNumber: true })}
                />
                {err.amount && (
                    <p className="text-xs text-red-500">{err.amount.message}</p>
                )}
            </div>
            <div>
                <Label>{m.expense_date()}</Label>
                <Input type="date" {...form.register('expense_date')} />
                {err.expense_date && (
                    <p className="text-xs text-red-500">
                        {err.expense_date.message}
                    </p>
                )}
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <Label>{m.expense_payment_method()}</Label>
                    <Select
                        value={form.watch('payment_method')}
                        onValueChange={(v) =>
                            form.setValue(
                                'payment_method',
                                v as ExpensePaymentMethod,
                            )
                        }
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="cash">
                                {m.expense_payment_method_cash()}
                            </SelectItem>
                            <SelectItem value="bank_transfer">
                                {m.expense_payment_method_bank_transfer()}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>{m.expense_payment_status()}</Label>
                    <Select
                        value={form.watch('payment_status')}
                        onValueChange={(v) =>
                            form.setValue(
                                'payment_status',
                                v as ExpensePaymentStatus,
                            )
                        }
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="paid">
                                {m.expense_payment_status_paid()}
                            </SelectItem>
                            <SelectItem value="unpaid">
                                {m.expense_payment_status_unpaid()}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div>
                <Label>{m.expense_note()}</Label>
                <textarea
                    {...form.register('note')}
                    className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
                    rows={2}
                />
            </div>
            <DialogFooter>
                <Button type="submit" disabled={submitting}>
                    {submitting ? '...' : m.expense_new()}
                </Button>
            </DialogFooter>
        </form>
    )
}
